import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tab, Tabs } from '@wfp/react';
import { Text, tooltipStyle } from '@wfp/react';
import { Tooltip } from 'acr_ui/sources/components/Tooltip';
import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import type { Location } from 'react-router-dom';
import { NavLink } from 'react-router-dom';
import Select from 'react-select';

import NoAccess from 'src/components/NoAccess';
import { DATA_STATUS_CHECK_LINK } from 'src/constants';
import ACRMultiFilesUploadButton from './ACRMultiFilesUploadButton';
import ExportTablesButton from './ExportTablesButton';
import HeaderExportButton from './HeaderExportButton';
import {
  PROJECT_STATUS,
  PUBLICATION_STATUS,
  REVIEW_STATUS,
  useReportsListData,
} from './hooks/useReportsListData';
import ProjectStatusTab from './ProjectStatusTab';
import PublicationActionManager from './PublicationActionManager';
import PublicationStatusTab from './PublicationStatusTab';
import ReviewStatusTab from './ReviewStatusTab';
import {
  createFilterSelect,
  createGetOptions,
  DashboardType,
  dashboardTypes,
  filterText,
  generateReviewIcons,
  headers,
  ReportData,
  useDateHandlers,
} from './utils/reportsUtils';

interface PeriodOption {
  value: string;
  label: string;
}

interface ReportsListPresentationProps {
  location: Location;
  isFetching: boolean;
  data: ReportData[];
  listStatus: DashboardType;
  handlePeriodOnChange: (option: PeriodOption) => void;
  periods: PeriodOption[];
  currentPeriod?: string;
}

interface AppConfig {
  userInfo?: {
    roles?: string[];
  };
  currentPeriod?: string;
}

interface RootState {
  viewData?: {
    GET_APP_CONFIGURATION?: {
      data?: AppConfig;
    };
  };
}

interface TabNavLinkProps {
  href: string;
  label: string;
}

const ReportsList: React.FC = () => {
  const {
    listStatus,
    reportListData,
    periods,
    currentPeriod,
    handlePeriodOnChange,
    location,
  } = useReportsListData();

  const { isFetching, data } = reportListData;

  return (
    <>
      <Helmet>
        <title>{dashboardTypes[listStatus]}</title>
      </Helmet>
      <ReportsListPresentation
        location={location}
        isFetching={isFetching}
        data={data || []}
        listStatus={listStatus}
        handlePeriodOnChange={handlePeriodOnChange}
        periods={periods}
        currentPeriod={currentPeriod}
      />
    </>
  );
};

export const TabNavLink: React.FC<TabNavLinkProps> = ({ href, label }) => {
  return (
    <NavLink
      to={{ hash: href }}
      data-test-id={`${label.toLowerCase().replace(' ', '-')}-link`}
    >
      {label}
    </NavLink>
  );
};

export const ReportsListPresentation: React.FC<
  ReportsListPresentationProps
> = props => {
  const appConfig = useSelector(
    (state: RootState) => state.viewData?.GET_APP_CONFIGURATION?.data,
  );
  const roles = appConfig?.userInfo?.roles;
  const isHqAdmin = roles && roles.includes('hq_admin');

  const currentExerciseYear = appConfig?.currentPeriod;

  const dashboardKeysList = useMemo(() => {
    const list: DashboardType[] = [PROJECT_STATUS, REVIEW_STATUS];
    if (isHqAdmin) list.push(PUBLICATION_STATUS);

    return list;
  }, [isHqAdmin]);

  const { handleDateChange, handleDateReset } = useDateHandlers(
    props.currentPeriod,
  );

  const hash = props.location.hash
    ? props.location.hash.slice(1)
    : PROJECT_STATUS;

  const getOptions = createGetOptions(
    props.data,
    props.listStatus,
    props.currentPeriod,
  );
  const filterSelect = createFilterSelect(getOptions);

  const selectedValue: PeriodOption | null = props.currentPeriod
    ? { value: props.currentPeriod, label: props.currentPeriod }
    : null;
  const selectedIndex = Object.keys(dashboardTypes).findIndex(
    item => item === hash,
  );

  if (props.listStatus === 'publication' && roles && !isHqAdmin) {
    return <NoAccess />;
  }

  return (
    <>
      <aside className="main-side-padding">
        <nav>
          <Tabs selected={selectedIndex}>
            {dashboardKeysList.map((key, idx) => {
              return (
                <Tab
                  selected={idx === selectedIndex}
                  renderAnchor={TabNavLink as any}
                  href={key}
                  label={dashboardTypes[key]}
                  key={key}
                />
              );
            })}
          </Tabs>
        </nav>
      </aside>

      <aside className="acr-dashboard-management-controls main-side-padding">
        <div className="acr-dashboard-period-picker-container">
          <div className="acr-dashboard-period-picker">
            <Text
              className="acr-dashboard-period-picker-label wfp--label"
              data-test-id="period-label"
            >
              Period
            </Text>
            <Select
              classNamePrefix="wfp--react-select"
              name="projectTypeFilters"
              isSearchable={false}
              options={props.periods}
              value={selectedValue}
              id="periodsSelectId"
              onChange={props.handlePeriodOnChange}
              isDisabled={props.isFetching}
            />
          </div>
        </div>

        <div className="pull-right">
          <div className="projects-list-btns" style={{ display: 'flex' }}>
            {props.listStatus === 'publication' ? (
              <>
                <ACRMultiFilesUploadButton period={props.currentPeriod} />
                <PublicationActionManager
                  data={props.data}
                  currentPeriod={props.currentPeriod}
                  headers={headers}
                />
              </>
            ) : (
              <>
                {props.currentPeriod === currentExerciseYear &&
                  props.listStatus === 'project' && (
                    <Tooltip
                      title={
                        <>
                          {`Click here to view the ${currentExerciseYear} ACR data`}{' '}
                          <br /> entry status and readiness overview
                        </>
                      }
                      trigger="mouseenter"
                      tooltipStyle={tooltipStyle}
                      theme="light"
                      position="bottom"
                      className="data-status-check-tooltip"
                    >
                      <a
                        className="external-link-btn"
                        target="_blank"
                        href={DATA_STATUS_CHECK_LINK}
                        rel="noreferrer"
                      >
                        {currentExerciseYear} Data Status Check
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                      </a>
                    </Tooltip>
                  )}
                <HeaderExportButton />
                <ExportTablesButton />
              </>
            )}
          </div>
        </div>
      </aside>

      <div className="reports-list">
        {props.listStatus === 'project' && (
          <ProjectStatusTab
            data={props.data}
            loading={props.isFetching}
            filterText={filterText}
            filterSelect={filterSelect}
            handleDateChange={handleDateChange}
            handleDateReset={handleDateReset}
            period={props.currentPeriod}
          />
        )}

        {props.listStatus === 'review' && (
          <ReviewStatusTab
            data={props?.data}
            loading={props.isFetching}
            filterText={filterText}
            filterSelect={filterSelect}
            generateReviewIcons={generateReviewIcons}
            period={props.currentPeriod}
          />
        )}

        {props.listStatus === 'publication' && (
          <PublicationStatusTab
            data={props.data}
            period={props.currentPeriod}
            filterText={filterText}
            filterSelect={filterSelect}
          />
        )}
      </div>
    </>
  );
};

export default ReportsList;
